// Watch it build: friendly step groups between the narration, the one App card of the thread, the live status line
// and the optional next-step chips (one tap sends them). Presentation of what the turn already did; the
// approval, question and action cards, the receipts and the turn's own error stay exactly where they are.
import { useEffect, useState, type FC } from 'react';
import { AuiIf, ThreadPrimitive, useAuiState } from '@assistant-ui/react';
import { ChevronDownIcon } from 'lucide-react';
import { useNamespaceCopy } from '@/i18n/ns';
import buildWatchCopy from '@/i18n/ns/build-watch';
import chatChipCopy from '@/i18n/ns/chat-chip';
import { useQuestionGate } from '@/billing/context';
import artifactPanelsCopy from '@/i18n/ns/artifact-panels';
import { formatCopy } from '@/i18n/ns';
import appPublishCopy from '@/i18n/ns/app-publish';
import { useChatInterfaceLocale } from '@/lib/chat-i18n';
import { useOptionalWorkspace } from '@/workspace/context';
import { activityTrace } from './activity-trace';
import { ActivityTraceView } from './activity-trace-view';
import {
  countSuffix,
  groupLabel,
  isAppTool,
  previewSuggestions,
  buildCanvasTarget,
  showsBuild,
  statusText,
  threadApp,
  type ThreadMessageLike,
} from './build-groups';
import { useAppBuild } from '@/workspace/apps/preview/live-build';
import './build-experience.css';

type AnyPart = NonNullable<ThreadMessageLike['parts']>[number];

/** True when this message is an App turn: it uses the App kit's tools, so its steps read as a build. */
export const isBuildMessage = (parts: readonly AnyPart[]) =>
  parts.some((part) => part.type === 'tool-call' && isAppTool(part.toolName));

function relativeTime(then: number, now: number, locale: string): string {
  const seconds = Math.round((then - now) / 1000);
  const units: [Intl.RelativeTimeFormatUnit, number][] = [
    ['day', 86400],
    ['hour', 3600],
    ['minute', 60],
  ];
  const format = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  for (const [unit, size] of units)
    if (Math.abs(seconds) >= size) return format.format(Math.round(seconds / size), unit);
  return format.format(seconds, 'second');
}

const AppCard: FC<{ parts: readonly AnyPart[] }> = () => {
  const copy = useNamespaceCopy(buildWatchCopy);
  const panels = useNamespaceCopy(artifactPanelsCopy);
  const locale = useChatInterfaceLocale();
  const workspace = useOptionalWorkspace();
  const running = useAuiState((s) => s.thread.isRunning);
  const messages = useAuiState((s) => s.thread.messages) as unknown as ThreadMessageLike[];
  const app = threadApp(messages, workspace?.threadId);
  if (!app.latest || app.latest.kind !== 'app-preview') return null;
  const name = app.latest.title || (app.appKind === 'deck' ? copy.yourDeck : copy.yourApp);
  const last = messages.find((message) => message.id === app.latestMessageId) as
    | (ThreadMessageLike & { createdAt?: Date | string })
    | undefined;
  const at = last?.createdAt ? new Date(last.createdAt).getTime() : null;
  const age = at === null ? null : Date.now() - at;
  const sub = running
    ? app.previews > 1
      ? copy.cardUpdating
      : copy.cardBuilding
    : age === null
      ? ''
      : age < 120_000
        ? copy.cardUpdatedJustNow
        : formatCopy(panels.libraryUpdated, {
            time: relativeTime(at as number, Date.now(), locale),
          });
  return (
    <button
      type="button"
      className="pc-build-card"
      data-slot="app-card"
      aria-label={sub ? `${name}, ${sub}` : name}
      onClick={() => app.latest && workspace?.openCanvas(app.latest)}
    >
      <span className="pc-build-card-tile" aria-hidden="true">
        {name.slice(0, 1).toUpperCase()}
      </span>
      <span className="pc-build-card-text">
        <strong>{name}</strong>
        {sub && <small>{sub}</small>}
      </span>
      <ChevronDownIcon aria-hidden className="pc-build-card-chevron" />
    </button>
  );
};

/** One run of consecutive tool calls between two pieces of narration, as one quiet row; the App card follows the group in which the App first exists. */
export const BuildStepGroup: FC<{ indices: readonly number[] }> = ({ indices }) => {
  const copy = useNamespaceCopy(buildWatchCopy);
  const locale = useChatInterfaceLocale();
  const workspace = useOptionalWorkspace();
  const messageId = useAuiState((s) => s.message.id);
  const messageParts = useAuiState((s) => s.message.parts) as unknown as AnyPart[];
  const messages = useAuiState((s) => s.thread.messages) as unknown as ThreadMessageLike[];
  const [open, setOpen] = useState(false);
  const parts = indices.map((index) => messageParts[index]).filter(Boolean);
  const app = threadApp(messages, workspace?.threadId);
  const ownsFirst = Boolean(
    app.first &&
    app.first.messageId === messageId &&
    parts.some((part) => part.toolCallId === app.first?.toolCallId),
  );
  const firstIndex =
    app.first?.messageId === messageId
      ? messageParts.findIndex((part) => part.toolCallId === app.first?.toolCallId)
      : -1;
  // "Updated the App" once an earlier preview exists (an earlier message, or an earlier group of this one).
  const previewBefore =
    Boolean(app.first) &&
    !ownsFirst &&
    (app.first?.messageId !== messageId || firstIndex < indices[0]);
  const label = groupLabel(parts, copy, previewBefore, app.appKind);
  const show = useNamespaceCopy(appPublishCopy).show;
  // Only a group that builds the App (writes, builds or previews it) names it; explore-only groups stay plain.
  const isBuild = parts.some((part) => part.type === 'tool-call' && isAppTool(part.toolName ?? ''));
  const trace = open
    ? activityTrace(`${messageId}:build:${indices[0]}`, parts as never, [], locale)
    : null;
  return (
    <div
      className="pc-build-group"
      data-slot="build-group"
      data-running={label.running || undefined}
    >
      <button
        type="button"
        className="pc-build-group-row"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        {label.running && <span className="pc-build-spinner" aria-hidden="true" />}
        <span className="pc-build-group-label">
          {label.label}
          <span className="pc-build-group-count">{countSuffix(label, copy, locale)}</span>
        </span>
        <ChevronDownIcon aria-hidden className="pc-build-chevron" data-open={open || undefined} />
      </button>
      {isBuild && workspace && !showsBuild(workspace.canvas) && (
        <button
          type="button"
          className="pc-build-show-step"
          data-slot="build-group-show"
          aria-label={`${label.label} · ${show}`}
          onClick={() => workspace.openCanvas(buildCanvasTarget(messages, workspace.threadId))}
        >
          {show}
        </button>
      )}
      {trace && <ActivityTraceView trace={trace} tab="steps" />}
      {ownsFirst && <AppCard parts={parts} />}
    </div>
  );
};

/** While an App turn runs: "{App name}: {latest narration or running step}", one line above the composer. */
export const BuildStatusLine: FC = () => {
  const copy = useNamespaceCopy(buildWatchCopy);
  const show = useNamespaceCopy(appPublishCopy).show;
  const workspace = useOptionalWorkspace();
  const build = useAppBuild(workspace?.threadId);
  const running = useAuiState((s) => s.thread.isRunning);
  const messages = useAuiState((s) => s.thread.messages) as unknown as ThreadMessageLike[];
  if (!running) return null;
  const buildThread = messages.some((message) =>
    isBuildMessage((message.parts ?? message.content ?? []) as AnyPart[]),
  );
  if (!buildThread) return null;
  const last = messages.at(-1);
  const status = statusText((last?.parts ?? last?.content ?? []) as AnyPart[], copy);
  if (!status) return null;
  const app = threadApp(messages, workspace?.threadId);
  // The name the chat gave the App while building it, else its preview's, else "Your App".
  const name =
    build?.title ||
    (app.latest && app.latest.kind === 'app-preview' && app.latest.title
      ? app.latest.title
      : app.appKind === 'deck'
        ? copy.yourDeck
        : copy.yourApp);
  const line = `${name}: ${status}`;
  // The canvas was closed (or shows something else): the line is the way back to the App being built.
  if (workspace && !showsBuild(workspace.canvas))
    return (
      <p className="pc-build-status" role="status" data-slot="build-status">
        <button
          type="button"
          className="pc-build-status-show"
          data-slot="build-status-show"
          aria-label={`${line} · ${show}`}
          onClick={() => workspace.openCanvas(buildCanvasTarget(messages, workspace.threadId))}
        >
          <span className="pc-build-spinner" aria-hidden="true" />
          <span>{line}</span>
          <span className="pc-build-show" aria-hidden="true">
            {show}
          </span>
        </button>
      </p>
    );
  return (
    <p className="pc-build-status" role="status" data-slot="build-status">
      <span className="pc-build-spinner" aria-hidden="true" />
      <span>{line}</span>
    </p>
  );
};

/** How long the tapped chip reads "Sent" before the row goes (the turn it started takes over the thread). */
const SENT_MS = 1400;

/**
 * After the turn: the optional next-step phrases of the latest preview, as chips. One tap SENDS the phrase as the
 * person's next message, through the same path as the composer's Send (the chat's model and effort, the selection chip
 * and the question/billing gate all apply); the composer is never filled. Signed out under required auth, a tap only
 * fills the composer and opens sign-in, as every other suggestion does. The tapped chip says "Sent" for a moment.
 */
export const BuildSuggestionChips: FC = () => {
  const workspace = useOptionalWorkspace();
  const copy = useNamespaceCopy(chatChipCopy);
  const { needsAuth, requestAuth } = useQuestionGate();
  const messages = useAuiState((s) => s.thread.messages) as unknown as ThreadMessageLike[];
  const [held, setHeld] = useState<{ chips: string[]; sent: string } | null>(null);
  useEffect(() => {
    if (!held) return;
    const timer = setTimeout(() => setHeld(null), SENT_MS);
    return () => clearTimeout(timer);
  }, [held]);
  const last = messages.at(-1);
  const live =
    last && last.role === 'assistant'
      ? previewSuggestions((last.parts ?? last.content ?? []) as AnyPart[], workspace?.threadId)
      : [];
  // The new turn replaces the answer these chips belonged to at once; the row stays for the "Sent" moment.
  const chips = held ? held.chips : live;
  return (
    <AuiIf condition={(s) => Boolean(held) || !s.thread.isRunning}>
      {chips.length > 0 && (
        <div className="pc-build-chips" data-slot="build-chips">
          {chips.map((chip) => {
            const sent = held?.sent === chip;
            return (
              <ThreadPrimitive.Suggestion
                key={chip}
                className="pc-build-chip"
                data-sent={sent || undefined}
                disabled={Boolean(held)}
                prompt={chip}
                {...(needsAuth
                  ? { onClick: requestAuth }
                  : { send: true, onClick: () => setHeld({ chips: live, sent: chip }) })}
              >
                {sent ? copy.sent : chip}
              </ThreadPrimitive.Suggestion>
            );
          })}
        </div>
      )}
    </AuiIf>
  );
};
